/* GOBA: complete-kit toggle (product page) + one-click cart add-ons */
import { CartAddEvent } from '@theme/events';

const money = (cents) => {
  const v = cents / 100;
  return '$' + (Number.isInteger(v) ? v.toString() : v.toFixed(2));
};

const cartSectionIds = () =>
  Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

async function addItems(items, source) {
  const sections = cartSectionIds();
  const res = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items, sections: sections.join(',') }),
  });
  const data = await res.json();
  if (data.status) throw new Error(data.description || data.message || 'Could not add to cart');
  let cart;
  try {
    cart = await (await fetch('/cart.js')).json();
  } catch (e) {}
  document.dispatchEvent(
    new CartAddEvent(cart, source, {
      source,
      itemCount: items.reduce((s, i) => s + i.quantity, 0),
      sections: data.sections,
    })
  );
  return data;
}

/* ---------- Complete kit toggle ---------- */
class GobaKit extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.productId = this.dataset.productId;
    this.threshold = Number(this.dataset.threshold) || 0;
    this.bottles = JSON.parse(this.querySelector('[data-kit-bottles]').textContent);
    this.lids = JSON.parse(this.querySelector('[data-kit-lids]').textContent);
    this.types = JSON.parse(this.querySelector('[data-kit-types]').textContent);
    this.check = this.querySelector('input[type="checkbox"]');

    this.refresh = this.refresh.bind(this);
    this.onCartUpdate = this.onCartUpdate.bind(this);

    document.addEventListener('variant:update', () => setTimeout(this.refresh, 60));
    document.addEventListener('change', () => setTimeout(this.refresh, 60));
    document.addEventListener('cart:update', this.onCartUpdate);
    window.__gobaKitOn = window.__gobaKitOn || {};
    this.check.addEventListener('change', () => {
      window.__gobaKitOn[this.productId] = this.check.checked;
      this.refresh();
    });
    this.refresh();
  }

  mainForm() {
    const forms = document.querySelectorAll(`product-form-component[data-product-id="${this.productId}"]`);
    for (const f of forms) {
      if (!(f.dataset.sectionId || '').startsWith('addon-')) return f;
    }
    return null;
  }

  currentVariantId() {
    const f = this.mainForm();
    const input = f && f.querySelector('input[name="id"]');
    return input ? String(input.value) : null;
  }

  kitFor(variantId) {
    const b = this.bottles[variantId];
    if (!b) return null;
    const [type, color, price] = b;
    const lids = this.types
      .filter((t) => t !== type)
      .map((t) => ({ type: t, lid: this.lids[`${t} / ${color}`] }))
      .filter((x) => x.lid && x.lid[1]);
    return {
      color,
      price,
      lids,
      extra: lids.reduce((s, x) => s + x.lid[2], 0),
    };
  }

  refresh() {
    if (!this.isConnected) return;
    const on = !!(window.__gobaKitOn && window.__gobaKitOn[this.productId]);
    this.check.checked = on;
    this.classList.toggle('is-on', on);
    const kit = this.kitFor(this.currentVariantId());
    if (!kit || kit.lids.length === 0) {
      this.hidden = true;
      return;
    }
    this.hidden = false;
    const n = kit.lids.length;
    this.querySelector('[data-kit-count]').textContent = n === 3 ? 'other 3' : `other ${n}`;
    this.querySelector('[data-kit-color]').textContent = kit.color;
    this.querySelector('[data-kit-list]').textContent = kit.lids.map((x) => x.type).join(', ');
    this.querySelector('[data-kit-extra]').textContent = '+' + money(kit.extra);
    const total = kit.price + kit.extra;
    const totalEl = this.querySelector('[data-kit-total]');
    totalEl.textContent =
      `Kit total ${money(total)}.` + (this.threshold && total >= this.threshold ? ' Ships free in the US.' : '');
  }

  async onCartUpdate(event) {
    const d = event.detail && event.detail.data;
    if (!this.isConnected || event.__gobaKitHandled) return;
    if (!d || d.didError || d.source !== 'product-form-component') return;
    if (String(d.productId) !== String(this.productId)) return;
    if (!(window.__gobaKitOn && window.__gobaKitOn[this.productId]) || this._busy) return;

    const variantId = this.bottles[String(event.detail.sourceId)] ? String(event.detail.sourceId) : this.currentVariantId();
    const kit = this.kitFor(variantId);
    if (!kit || !kit.lids.length) return;

    event.__gobaKitHandled = true;
    const qty = Number(d.itemCount) || 1;
    this._busy = true;
    try {
      await addItems(
        kit.lids.map((x) => ({ id: Number(x.lid[0]), quantity: qty })),
        'goba-kit'
      );
    } catch (e) {
      console.error(e);
    } finally {
      this._busy = false;
    }
  }
}
if (!customElements.get('goba-kit')) customElements.define('goba-kit', GobaKit);

/* ---------- One-click cart add-ons ---------- */
if (!window.__gobaAddonsBound) {
  window.__gobaAddonsBound = true;
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-goba-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;
    const label = btn.textContent;
    btn.textContent = 'Adding';
    try {
      await addItems([{ id: Number(btn.dataset.gobaAdd), quantity: 1 }], 'goba-upsell');
    } catch (err) {
      console.error(err);
      btn.textContent = 'Try again';
      btn.disabled = false;
      return;
    }
    btn.textContent = label;
    btn.disabled = false;
  });
}
